import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
  alpha,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Warning,
  Error,
  CheckCircle,
  ShowChart,
  ArrowDownward,
  ArrowUpward,
  Tag,
  Settings,
  Bolt,
  ReportProblem,
  VerifiedUser,
  Rule,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { motion, AnimatePresence } from 'framer-motion';
import MiniMeterChart from './MiniMeterChart';
import { MeterMaintenanceModal } from './MeterMaintenanceModal';
import AcknowledgeWarningPanel, { WarningTrack } from './AcknowledgeWarningPanel';
import AcknowledgementHistory from './AcknowledgementHistory';
import { useCapabilities } from '../contexts/CapabilitiesContext';

interface ProductData {
  productType: string;
  meterProductId: string;   // real registry id (e.g. "BAY 25 POS 2_MPU10E10_HIGH")
  flowLabel?: string;   // 'HIGH' | 'LOW' | 'MID…' for multi-flow meters
  health: 'red' | 'yellow' | 'green' | 'unknown';
  healthReasons?: string[];
  /** Health AFTER acknowledgement suppression -- what the card renders. `health` stays the truth. */
  displayHealth?: 'red' | 'yellow' | 'green' | 'unknown';
  /** Present only while an acknowledgement is active on this track. */
  acknowledgement?: {
    by?: string;
    at?: string;
    reason_code?: string;
    reason_label?: string;
    note?: string;
    expires_at?: string;
    consecutive?: number;
  };
  currentFactor: number;
  previousFactor: number;
  controlLimits: {
    ucl: number;
    lcl: number;
    center: number;
  };
  statistics: {
    documentCount: number;
    avgMeterFactor: number;
    trend: 'increasing' | 'decreasing' | 'stable' | 'unknown';
    anomalyCount: number;
    daysSinceProving: number;
  };
  lastProvingDate?: string;
  // Spike and Bias detection fields
  spikeDetected?: boolean;
  spikeCount?: number;
  spikeDetails?: {
    changePercent: number;
    timestamp: string;
  };
  biasDetected?: boolean;
  biasDetails?: {
    severity: 'high' | 'medium' | 'low';
    consecutiveCount: number;
  };
  anomalyType?: 'none' | 'spike' | 'bias';
  // Regression forecast/drift signal (from the registry).
  drift?: {
    drift_flag?: boolean;
    trust_level?: string;
    oos_r_squared?: number;
    forecast_expected?: number;
    forecast_pi_lower?: number;
    forecast_pi_upper?: number;
    consecutive_exceptions?: number;
    last_exception?: boolean;
  };
  // HM 11 repeatability validity gate status (from the registry).
  repeatability?: {
    enabled?: boolean;
    flag?: boolean;
    latest_pass?: boolean;
    latest_value?: number;
    limit?: number;
    action?: string;
    failure_count?: number;
  };
  // Meter-factor adjustment status for the LATEST prove (from the registry).
  adjustment?: {
    latest_has_adjustment?: boolean;
    latest_label?: string;
  };
}

interface GroupedMeterTileProps {
  meterId: string;
  products: ProductData[];
  meterDirection?: 'inbound' | 'outbound' | null;
  meterType?: 'custody' | 'check' | null;
  onClick?: () => void;
  onViewChart?: (meterProductId: string, meterId: string, productType: string) => void;
  onDirectionChange?: (meterId: string, direction: 'inbound' | 'outbound') => void;
  onTypeChange?: (meterId: string, meterType: 'custody' | 'check') => void;
  onViewModeChange?: (meterId: string, mode: 'numbers' | 'chart') => void;
  /** Called after maintenance is added or deleted so the grid can refetch this meter's data in place. */
  onMeterChanged?: () => void | Promise<void>;
  isDragging?: boolean;
  forceViewMode?: 'numbers' | 'chart';
  // When the grid is filtered by a product (or a health status), the matching
  // product tab is auto-selected so the user sees the relevant product directly.
  focusProduct?: string;
  // Exact track (meter_product_id) to auto-select -- disambiguates multi-flow HIGH/LOW
  // tracks that share a base product_type. Preferred over focusProduct.
  focusMpid?: string;
}

const GroupedMeterTile: React.FC<GroupedMeterTileProps> = ({
  meterId,
  products,
  meterDirection,
  meterType,
  onClick,
  onViewChart,
  onDirectionChange,
  onTypeChange,
  onViewModeChange,
  onMeterChanged,
  isDragging = false,
  forceViewMode,
  focusProduct,
  focusMpid,
}) => {
  const theme = useTheme();
  const { getIdTokenClaims } = useAuth0();
  const { can } = useCapabilities();  // RBAC: gate meter direction/type on configure_meters
  const canConfigureMeters = can('configure_meters');
  // Acknowledging reuses the maintenance capability -- same people who can reset a baseline (decision 8).
  const canAcknowledge = can('run_maintenance');
  const [activeTab, setActiveTab] = useState(0);
  // Auto-select the focused product's tab when the grid filter targets a product or
  // health status. Prefer the exact track (meter_product_id) so a multi-flow HIGH/LOW
  // that shares a base product_type resolves to the specific flagged track, not just the
  // first one. Deliberately depends ONLY on the focus values -- NOT on `products` -- so a
  // silent 45s data refresh (new products array, same focus) does not yank the user's
  // manual product selection back to the focused/first tab.
  useEffect(() => {
    if (focusMpid) {
      const i = products.findIndex((p) => p.meterProductId === focusMpid);
      if (i >= 0) { setActiveTab(i); return; }
    }
    if (focusProduct) {
      const i = products.findIndex((p) => p.productType === focusProduct);
      if (i >= 0) setActiveTab(i);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMpid, focusProduct]);
  const [localDirection, setLocalDirection] = useState<'inbound' | 'outbound' | null>(meterDirection || 'inbound');
  const [savingDirection, setSavingDirection] = useState(false);
  const [localType, setLocalType] = useState<'custody' | 'check' | null>(meterType || 'custody');
  const [savingType, setSavingType] = useState(false);
  const [viewMode, setViewMode] = useState<'numbers' | 'chart'>('numbers');
  const [chartData, setChartData] = useState<any[]>([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [maintenanceModalOpen, setMaintenanceModalOpen] = useState(false);
  const [selectedProductForMaintenance, setSelectedProductForMaintenance] = useState<ProductData | null>(null);
  // Acknowledge-warnings dialogs. History is keyed by track so the ✓ chip and the panel can both open it.
  const [ackPanelOpen, setAckPanelOpen] = useState(false);
  const [historyTrack, setHistoryTrack] = useState<ProductData | null>(null);

  // Compact, DISTINGUISHING tab labels. A plain 3-char abbreviation collapses e.g.
  // MSUPERUNLEADED and MSUPERUNLEADED97 both to "MSU". We only disambiguate when two
  // TABS would look identical — i.e. two DIFFERENT products share the same base
  // abbreviation AND collide as tabs: same flow (2 HI or 2 LO), or at least one is a
  // no-flow "normal" tab. HI + LO of the same product (or a HI of one + LO of another)
  // are already distinguishable by their badge, so those keep the plain abbreviation.
  const productLabels = useMemo(() => {
    const map: { [k: string]: string } = {
      PREM: 'PR', ETH: 'ET', REG: 'RG', CRUDE: 'CR', DIESEL: 'DS', JET: 'JT', OSU: 'OSU', OSP: 'OSP',
    };
    const base = (pt: string) => map[pt] || pt.slice(0, 3).toUpperCase();
    const commonPrefix = (arr: string[]) => {
      if (!arr.length) return '';
      let p = arr[0];
      for (const s of arr) { while (p && !s.startsWith(p)) p = p.slice(0, -1); }
      return p;
    };

    // distinct product types + the set of flow labels each one appears with on this card
    const flowsOf: { [pt: string]: Set<string> } = {};
    for (const p of products) {
      (flowsOf[p.productType] = flowsOf[p.productType] || new Set()).add(p.flowLabel || '');
    }
    const distinct = Object.keys(flowsOf);

    // group by base abbreviation
    const byBase: { [b: string]: string[] } = {};
    for (const pt of distinct) (byBase[base(pt)] = byBase[base(pt)] || []).push(pt);

    // a pair (A,B) with the same base conflicts if any of their tabs are confusable:
    // they share a flow, or either one has a no-flow ("normal") tab.
    const needsDisambig = new Set<string>();
    for (const b of Object.keys(byBase)) {
      const pts = byBase[b];
      if (pts.length < 2) continue;
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const A = flowsOf[pts[i]], B = flowsOf[pts[j]];
          const shareFlow = Array.from(A).some((f) => f && B.has(f));
          if (shareFlow || A.has('') || B.has('')) {
            needsDisambig.add(pts[i]); needsDisambig.add(pts[j]);
          }
        }
      }
    }

    const labels: { [k: string]: string } = {};
    for (const pt of distinct) {
      const b = base(pt);
      if (!needsDisambig.has(pt)) { labels[pt] = b; continue; }
      const siblings = byBase[b].filter((o) => needsDisambig.has(o));
      const prefix = commonPrefix(siblings);
      const tail = pt.slice(prefix.length).toUpperCase().replace(/[^A-Z0-9]/g, '');
      labels[pt] = (b + tail).slice(0, 8);
    }
    return labels;
  }, [products]);

  // Load saved view preference from localStorage
  useEffect(() => {
    if (forceViewMode) {
      setViewMode(forceViewMode);
    } else {
      const savedViewMode = localStorage.getItem(`meter-view-${meterId}`);
      if (savedViewMode === 'chart' || savedViewMode === 'numbers') {
        setViewMode(savedViewMode);
      }
    }
  }, [meterId, forceViewMode]);

  // The mini chart's points and control limits come from /meters/{id}/history, which was only fetched when
  // the view or tab changed. After maintenance is added or deleted the grid refetches and hands down new
  // control limits, so the card's outline/health updated while the chart kept drawing the OLD baseline.
  // Re-key the fetch on the active product's limits + latest factor so the line and the dashed limits move
  // together.
  const activeProduct = products[activeTab];
  const limitsSignature = [
    activeProduct?.controlLimits?.center,
    activeProduct?.controlLimits?.ucl,
    activeProduct?.controlLimits?.lcl,
    activeProduct?.currentFactor,
    activeProduct?.lastProvingDate,
  ].join('|');

  // Fetch chart data when switching to chart view (or when this meter's baseline/limits change)
  useEffect(() => {
    if (viewMode === 'chart' && products.length > 0) {
      fetchChartData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, activeTab, limitsSignature]);

  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || '';

  const fetchChartData = async () => {
    setLoadingChart(true);
    const currentProduct = products[activeTab];

    try {
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      // Use the real registry id (handles multi-flow HIGH/LOW tracks where the
      // product_type no longer carries the flow suffix).
      const meterProductId = currentProduct.meterProductId;

      // Use exact same endpoint as MeterFactorChart
      const response = await fetch(`${API_ENDPOINT}/meters/${meterProductId}/history`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new (Error as any)('Failed to fetch meter history');
      }

      const data = await response.json();

      if (data.history && data.statistics) {
        // Process data exactly like MeterFactorChart does
        const processedData = data.history
          .map((point: any) => {
            const date = new Date(point.proving_timestamp);
            return {
              timestamp: point.proving_timestamp || point.timestamp,
              meter_factor: point.meter_factor,
              ucl: data.statistics.fixed_control_limits.ucl,
              lcl: data.statistics.fixed_control_limits.lcl,
              center: data.statistics.fixed_control_limits.center
            };
          })
          .sort((a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
          .slice(-5); // Last 5 points for mini chart

        if (processedData.length > 0) {
          setChartData(processedData);
          setLoadingChart(false);
          return;
        }
      }
    } catch (error) {
      console.error('Error fetching meter history:', error);
    }

    // Fallback to simple two-point chart if API fails or no history
    const simpleChartData = [
      {
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        meter_factor: currentProduct.previousFactor,
        ucl: currentProduct.controlLimits.ucl,
        lcl: currentProduct.controlLimits.lcl,
        center: currentProduct.controlLimits.center
      },
      {
        timestamp: new Date().toISOString(),
        meter_factor: currentProduct.currentFactor,
        ucl: currentProduct.controlLimits.ucl,
        lcl: currentProduct.controlLimits.lcl,
        center: currentProduct.controlLimits.center
      }
    ];

    setChartData(simpleChartData);
    setLoadingChart(false);
  };

  const toggleViewMode = () => {
    const newMode = viewMode === 'numbers' ? 'chart' : 'numbers';
    setViewMode(newMode);
    localStorage.setItem(`meter-view-${meterId}`, newMode);
    if (onViewModeChange) {
      onViewModeChange(meterId, newMode);
    }
  };

  const handleDirectionChange = async (event: React.MouseEvent<HTMLElement>, newDirection: 'inbound' | 'outbound' | null) => {
    if (!newDirection) return;

    setLocalDirection(newDirection);
    setSavingDirection(true);

    try {
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      await axios.put(
        `${API_ENDPOINT}/meter-direction/${meterId}`,
        { direction: newDirection },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (onDirectionChange) {
        onDirectionChange(meterId, newDirection);
      }
    } catch (error) {
      console.error('Failed to update meter direction:', error);
      // Revert on failure
      setLocalDirection(meterDirection || null);
    } finally {
      setSavingDirection(false);
    }
  };

  const handleTypeChange = async (event: React.MouseEvent<HTMLElement>, newType: 'custody' | 'check' | null) => {
    if (!newType) return;

    setLocalType(newType);
    setSavingType(true);

    try {
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      await axios.put(
        `${API_ENDPOINT}/meter-type/${meterId}`,
        { type: newType },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (onTypeChange) {
        onTypeChange(meterId, newType);
      }
    } catch (error) {
      console.error('Failed to update meter type:', error);
      // Revert on failure
      setLocalType(meterType || null);
    } finally {
      setSavingType(false);
    }
  };

  const handleOpenMaintenance = (product: ProductData) => {
    setSelectedProductForMaintenance(product);
    setMaintenanceModalOpen(true);
  };

  const handleCloseMaintenance = () => {
    setMaintenanceModalOpen(false);
    setSelectedProductForMaintenance(null);
  };

  const handleMaintenanceComplete = () => {
    // Maintenance changed this meter's baseline/health. Refetch the grid's data instead of reloading the
    // page — the modal no longer forces window.location.reload().
    void onMeterChanged?.();
  };

  // Get the worst health status across all products
  // An acknowledged warning renders as healthy (display_health) while `health` keeps the truth for the
  // acknowledge panel, the audit trail and rollback. Everything the eye reads uses shownHealth().
  const shownHealth = (p: ProductData) => p.displayHealth || p.health;
  const isAcked = (p: ProductData) => !!p.acknowledgement;

  const getOverallHealth = () => {
    if (products.some(p => shownHealth(p) === 'red')) return 'red';
    if (products.some(p => shownHealth(p) === 'yellow')) return 'yellow';
    if (products.some(p => shownHealth(p) === 'green')) return 'green';
    return 'unknown';
  };

  const overallHealth = getOverallHealth();
  const currentProduct = products[activeTab];

  /** Short track label for the dialogs, e.g. "KEROSENE HI". */
  const trackLabel = (p: ProductData) =>
    p.flowLabel
      ? `${p.productType} ${p.flowLabel === 'HIGH' ? 'HI' : p.flowLabel === 'LOW' ? 'LO' : p.flowLabel}`
      : p.productType;

  // Every non-green track on this physical meter, by TRUE health: 97 warnings sit on 39 meters, so the
  // panel has to offer the whole meter at once. Critical tracks are included so the panel can show why
  // they are blocked rather than silently hiding them.
  const ackTracks: WarningTrack[] = products
    .filter(p => p.health === 'yellow' || p.health === 'red')
    .map(p => ({
      meter_product_id: p.meterProductId,
      label: trackLabel(p),
      health: p.health,
      reasons: p.healthReasons || [],
      acknowledged: isAcked(p),
    }));

  // Get health color
  const getHealthColor = (health: string) => {
    switch (health) {
      case 'red':
        return theme.palette.error.main;
      case 'yellow':
        return theme.palette.warning.main;
      case 'green':
        return theme.palette.success.main;
      default:
        return theme.palette.grey[500];
    }
  };

  // Get health icon
  const getHealthIcon = (health: string) => {
    switch (health) {
      case 'red':
        return <Error sx={{ color: theme.palette.error.main }} />;
      case 'yellow':
        return <Warning sx={{ color: theme.palette.warning.main }} />;
      case 'green':
        return <CheckCircle sx={{ color: theme.palette.success.main }} />;
      default:
        return null;
    }
  };

  // Get trend icon
  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return <TrendingUp sx={{ fontSize: 16, color: theme.palette.info.main }} />;
      case 'decreasing':
        return <TrendingDown sx={{ fontSize: 16, color: theme.palette.warning.main }} />;
      default:
        return <TrendingFlat sx={{ fontSize: 16, color: theme.palette.text.secondary }} />;
    }
  };

  if (!currentProduct) return null;

  // Calculate factor change
  const factorChange = Number(currentProduct.currentFactor) - Number(currentProduct.previousFactor);
  const factorChangePercent = Number(currentProduct.previousFactor)
    ? (factorChange / Number(currentProduct.previousFactor)) * 100
    : 0;

  return (
    <motion.div
      data-meter-id={meterId}
      whileHover={{ scale: isDragging ? 1 : 1.01 }}
      animate={{
        opacity: isDragging ? 0.5 : 1,
        scale: isDragging ? 0.95 : 1,
      }}
      transition={{ duration: 0.2 }}
      style={{
        transition: 'all 0.3s ease-in-out',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Card
        sx={{
          height: '100%',
          maxHeight: '100%',
          position: 'relative',
          cursor: onClick ? 'pointer' : 'default',
          border: `2px solid ${alpha(getHealthColor(overallHealth), 0.3)}`,
          backgroundColor: alpha(getHealthColor(overallHealth), 0.02),
          '&:hover': {
            boxShadow: theme.shadows[4],
            borderColor: getHealthColor(overallHealth),
          },
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={onClick}
      >
        <CardContent sx={{ p: 1.5 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5, mb: 1 }}>
            {/* minWidth:0 lets this flex child shrink so the scrollable product
                tabs stay within bounds instead of pushing the header controls
                (chart toggle + status) off the card when there are many HI/LO tabs. */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography
                  variant="subtitle1"
                  title={meterId}
                  sx={{ fontWeight: 600, fontSize: '0.95rem', whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {meterId}
                </Typography>
                {/* Direction + Type toggles stacked vertically to keep the header narrow */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {/* Meter Direction Toggle */}
                <ToggleButtonGroup
                  value={localDirection}
                  exclusive
                  onChange={handleDirectionChange}
                  size="small"
                  disabled={savingDirection || !canConfigureMeters}
                  sx={{
                    height: 20,
                    '& .MuiToggleButton-root': {
                      py: 0,
                      px: 0.5,
                      fontSize: '0.65rem',
                      minWidth: 44,
                      justifyContent: 'center',
                      border: '1px solid',
                      borderColor: alpha(theme.palette.divider, 0.3),
                      '&.Mui-selected': {
                        bgcolor: localDirection === 'inbound' ? alpha(theme.palette.info.main, 0.1) : alpha(theme.palette.warning.main, 0.1),
                        borderColor: localDirection === 'inbound' ? theme.palette.info.main : theme.palette.warning.main,
                        color: localDirection === 'inbound' ? theme.palette.info.main : theme.palette.warning.main,
                        '&:hover': {
                          bgcolor: localDirection === 'inbound' ? alpha(theme.palette.info.main, 0.2) : alpha(theme.palette.warning.main, 0.2),
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton value="inbound">
                    <Tooltip title="Inbound: Receives product">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <ArrowDownward sx={{ fontSize: '0.75rem' }} />
                        IN
                      </Box>
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="outbound">
                    <Tooltip title="Outbound: Delivers product">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <ArrowUpward sx={{ fontSize: '0.75rem' }} />
                        OUT
                      </Box>
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>

                {/* Meter Type Toggle: Custody Transfer vs Check */}
                <ToggleButtonGroup
                  value={localType}
                  exclusive
                  onChange={handleTypeChange}
                  size="small"
                  disabled={savingType || !canConfigureMeters}
                  sx={{
                    height: 20,
                    '& .MuiToggleButton-root': {
                      py: 0,
                      px: 0.5,
                      fontSize: '0.65rem',
                      minWidth: 44,
                      justifyContent: 'center',
                      border: '1px solid',
                      borderColor: alpha(theme.palette.divider, 0.3),
                      '&.Mui-selected': {
                        bgcolor: localType === 'custody' ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.secondary.main, 0.1),
                        borderColor: localType === 'custody' ? theme.palette.success.main : theme.palette.secondary.main,
                        color: localType === 'custody' ? theme.palette.success.main : theme.palette.secondary.main,
                        '&:hover': {
                          bgcolor: localType === 'custody' ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.secondary.main, 0.2),
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton value="custody">
                    <Tooltip title="Custody Transfer: billing-grade meter">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <VerifiedUser sx={{ fontSize: '0.75rem' }} />
                        CT
                      </Box>
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="check">
                    <Tooltip title="Check: verification meter">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <Rule sx={{ fontSize: '0.75rem' }} />
                        CHK
                      </Box>
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
                </Box>
              </Box>

              {/* Product tabs — wrap onto multiple rows (into the card's spare
                  vertical space) instead of horizontal scroll, so every HI/LO
                  product stays visible on meters with many products. */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.25 }}>
                {products.map((product, index) => {
                  const isActive = activeTab === index;
                  const tip = [
                    product.flowLabel ? `${product.productType} (${product.flowLabel})` : product.productType,
                    product.healthReasons && product.healthReasons.length ? product.healthReasons.join(' • ') : '',
                    isAcked(product) ? `Acknowledged by ${product.acknowledgement?.by || 'a user'}` : '',
                  ].filter(Boolean).join(' — ');
                  return (
                    <Tooltip key={index} title={tip} arrow>
                      <Box
                        onClick={() => setActiveTab(index)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.25,
                          px: 0.5, py: 0.2, borderRadius: '4px', cursor: 'pointer',
                          border: '1px solid',
                          borderColor: isActive ? 'primary.main' : (t) => alpha(t.palette.divider, 0.5),
                          bgcolor: isActive ? (t) => alpha(t.palette.primary.main, 0.08) : 'transparent',
                          transition: 'background-color 0.15s, border-color 0.15s',
                          '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.12) },
                        }}
                      >
                        <Typography sx={{
                          fontSize: '0.7rem',
                          fontWeight: isActive ? 700 : 600,
                          color: isActive ? 'primary.main' : 'text.primary',
                          whiteSpace: 'nowrap',
                        }}>
                          {productLabels[product.productType] || product.productType.slice(0, 3).toUpperCase()}
                        </Typography>
                        {product.flowLabel && (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: '0.6rem', fontWeight: 700, lineHeight: 1,
                              px: 0.4, py: '1px', borderRadius: '3px', color: '#fff',
                              bgcolor: (t) =>
                                product.flowLabel === 'HIGH' ? t.palette.warning.main : t.palette.info.main,
                            }}
                          >
                            {product.flowLabel === 'HIGH' ? 'HI' : product.flowLabel === 'LOW' ? 'LO' : product.flowLabel}
                          </Typography>
                        )}
                        <Box sx={{
                          width: 5, height: 5, borderRadius: '50%',
                          bgcolor: getHealthColor(shownHealth(product)),
                        }} />
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 1.25 }}>
              <Tooltip title="Baseline Maintenance">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenMaintenance(currentProduct);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <Settings fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={viewMode === 'numbers' ? "View Control Chart" : "View Numbers"}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleViewMode();
                  }}
                  sx={{ p: 0.5 }}
                >
                  {viewMode === 'numbers' ? <ShowChart fontSize="small" /> : <Tag fontSize="small" />}
                </IconButton>
              </Tooltip>
              {shownHealth(currentProduct) === 'red' && (
                <Tooltip title={currentProduct.healthReasons ? `CRITICAL: ${currentProduct.healthReasons.join(' • ')}` : 'CRITICAL: Health issue detected'}>
                  <Error fontSize="small" sx={{ color: theme.palette.error.main }} />
                </Tooltip>
              )}
              {shownHealth(currentProduct) === 'yellow' && (
                /* Clicking the warning is how a warning gets acknowledged. Kept as a plain icon for users
                   without the capability, so the card looks the same but offers nothing that will 403. */
                <Tooltip title={
                  (currentProduct.healthReasons ? `WARNING: ${currentProduct.healthReasons.join(' • ')}` : 'WARNING: Monitoring required')
                  + (canAcknowledge ? ' — click to acknowledge' : '')
                }>
                  {canAcknowledge ? (
                    <IconButton size="small" sx={{ p: 0.25 }}
                                onClick={(e) => { e.stopPropagation(); setAckPanelOpen(true); }}>
                      <Warning fontSize="small" sx={{ color: theme.palette.warning.main }} />
                    </IconButton>
                  ) : (
                    <Warning fontSize="small" sx={{ color: theme.palette.warning.main }} />
                  )}
                </Tooltip>
              )}
              {shownHealth(currentProduct) === 'green' && (
                <CheckCircle fontSize="small" sx={{ color: theme.palette.success.main }} />
              )}
            </Box>
          </Box>

          {/* Condition flags on their OWN row below the header — so a wide chip
              (e.g. "Re-prove") never crowds the header controls. Severity-ranked
              (red first), capped with a "+N" overflow. Renders nothing when empty. */}
          {(() => {
            type Flag = { key: string; label: string; color: 'error' | 'warning'; tip: string };
            const flags: Flag[] = [];
            if (currentProduct.spikeDetected) {
              flags.push({
                key: 'spike',
                label: currentProduct.spikeCount && currentProduct.spikeCount > 1 ? `Spike ×${currentProduct.spikeCount}` : 'Spike',
                color: 'error',
                tip: currentProduct.spikeCount && currentProduct.spikeCount > 1
                  ? `${currentProduct.spikeCount} SPIKES detected (largest: ${currentProduct.spikeDetails?.changePercent?.toFixed(2) || '0.00'}%)`
                  : `SPIKE: ${currentProduct.spikeDetails?.changePercent?.toFixed(2) || '0.00'}% change detected`
              });
            }
            if (currentProduct.repeatability?.enabled && currentProduct.repeatability?.flag) {
              const rp = currentProduct.repeatability;
              const latestFailed = rp.latest_pass === false;
              flags.push({
                key: 'repeat',
                label: latestFailed ? 'Re-prove' : 'Repeatability',
                color: latestFailed ? 'error' : 'warning',
                tip: latestFailed
                  ? `RE-PROVE REQUIRED — the latest prove failed repeatability ` +
                    (rp.latest_value != null ? `(${rp.latest_value.toFixed(3)}% ` : '(') +
                    `> ${(rp.limit ?? 0.05).toFixed(2)}% limit, HM 11 §7.3.3). ` +
                    (rp.action === 'invalidate'
                      ? 'This prove is excluded from the numbers until a valid re-prove is recorded.'
                      : 'Flagged but kept in the numbers.')
                  : `${rp.failure_count ?? 0} past prove(s) failed repeatability (HM 11 §7.3); the latest prove is within the ${(rp.limit ?? 0.05).toFixed(2)}% tolerance.`
              });
            }
            if (currentProduct.biasDetected) {
              flags.push({
                key: 'bias', label: 'Bias', color: 'warning',
                tip: `BIAS: ${currentProduct.biasDetails?.consecutiveCount} consecutive readings after spike - ${currentProduct.biasDetails?.severity} severity`
              });
            }
            if (currentProduct.drift?.drift_flag) {
              flags.push({
                key: 'drift', label: 'Drift', color: 'warning',
                tip: `Drifting from forecast — the last ${currentProduct.drift.consecutive_exceptions} provings landed outside the expected range. Model trust: ${currentProduct.drift.trust_level}.` +
                  (currentProduct.drift.forecast_expected != null ? ` Expected next ≈ ${currentProduct.drift.forecast_expected.toFixed(4)}.` : '')
              });
            }
            if (currentProduct.adjustment?.latest_has_adjustment) {
              const lbl = currentProduct.adjustment.latest_label;
              flags.push({
                key: 'adjust', label: 'Adjusted', color: 'warning',
                tip: `The latest prove had a meter-factor adjustment${lbl ? ` (${lbl})` : ''}. Attributed to this flow track.`
              });
            }
            const acked = isAcked(currentProduct);
            if (!flags.length && !acked) return null;
            // Red (error) before amber (warning); stable within a color.
            flags.sort((a, b) => (a.color === 'error' ? 0 : 1) - (b.color === 'error' ? 0 : 1));
            const MAX_VISIBLE = 3;  // more room on its own row
            const shown = flags.slice(0, MAX_VISIBLE);
            const hidden = flags.slice(MAX_VISIBLE);
            const openChart = (e: any) => { e.stopPropagation(); onViewChart?.(currentProduct.meterProductId, meterId, currentProduct.productType); };
            const ack = currentProduct.acknowledgement;
            const streak = ack?.consecutive ?? 1;
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                {/* The acknowledged marker. Decision 6: an acknowledged warning reads as healthy but is
                    never invisible -- this chip is the record that someone parked it, and opens the history. */}
                {acked && (
                  <Tooltip title={
                    `Warning acknowledged by ${ack?.by || 'a user'}` +
                    (ack?.reason_label ? ` — ${ack.reason_label}` : '') +
                    (ack?.note ? ` (“${ack.note}”)` : '') +
                    (ack?.expires_at ? `. Returns ${new Date(ack.expires_at).toLocaleDateString()}` : '') +
                    (streak > 1 ? `. Acknowledged ${streak} times in a row.` : '') +
                    ' Click for history.'
                  }>
                    <Chip
                      size="small"
                      color={streak > 2 ? 'warning' : 'info'}
                      variant="outlined"
                      icon={<CheckCircle sx={{ fontSize: '0.8rem' }} />}
                      label={streak > 1 ? `Ack ×${streak}` : 'Acknowledged'}
                      onClick={(e: any) => { e.stopPropagation(); setHistoryTrack(currentProduct); }}
                      sx={{ height: 20, fontSize: '0.65rem', cursor: 'pointer' }}
                    />
                  </Tooltip>
                )}
                {shown.map((f) => (
                  <Tooltip key={f.key} title={f.tip}>
                    <Chip size="small" color={f.color} label={f.label} onClick={openChart}
                      sx={{ height: 20, fontSize: '0.65rem', cursor: 'pointer' }} />
                  </Tooltip>
                ))}
                {hidden.length > 0 && (
                  <Tooltip title={`Also: ${hidden.map((f) => f.label).join(' • ')}`}>
                    <Chip size="small" variant="outlined"
                      color={hidden.some((f) => f.color === 'error') ? 'error' : 'warning'}
                      label={`+${hidden.length}`} onClick={openChart}
                      sx={{ height: 20, fontSize: '0.65rem', cursor: 'pointer' }} />
                  </Tooltip>
                )}
              </Box>
            );
          })()}

          {/* Animated Content for Selected Product */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {/* Conditional display based on view mode */}
              {viewMode === 'numbers' ? (
                <>
                  {/* Meter Factor Display */}
                  <Box sx={{ my: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.25, fontSize: '1.2rem' }}>
                      {Number(currentProduct.currentFactor).toFixed(4)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Previous: {Number(currentProduct.previousFactor).toFixed(4)}
                      </Typography>
                      {getTrendIcon(currentProduct.statistics.trend)}
                      <Typography
                        variant="caption"
                        sx={{
                          // Green if within control limits, red if outside
                          color: (Number(currentProduct.currentFactor) >= Number(currentProduct.controlLimits.lcl) &&
                                  Number(currentProduct.currentFactor) <= Number(currentProduct.controlLimits.ucl))
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                        }}
                      >
                        {factorChange > 0 ? '+' : ''}{factorChangePercent.toFixed(2)}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* Control Limits Progress Bar */}
                  <Box sx={{ mb: 1 }}>
                    {(() => {
                      const lcl = Number(currentProduct.controlLimits.lcl);
                      const ucl = Number(currentProduct.controlLimits.ucl);
                      const current = Number(currentProduct.currentFactor);

                      // Calculate scale range - expand symmetrically if value is outside control limits
                      let minScale = lcl;
                      let maxScale = ucl;
                      let isScaled = false;

                      if (current < lcl || current > ucl) {
                        // Calculate how far outside the limits we are
                        const distanceBelowLCL = current < lcl ? lcl - current : 0;
                        const distanceAboveUCL = current > ucl ? current - ucl : 0;
                        const maxDistance = Math.max(distanceBelowLCL, distanceAboveUCL);

                        // Add symmetric padding on both sides
                        const padding = maxDistance + (ucl - lcl) * 0.1; // Add 10% of range as extra padding
                        minScale = lcl - padding;
                        maxScale = ucl + padding;
                        isScaled = true;
                      }

                      const range = maxScale - minScale;
                      const lclPosition = ((lcl - minScale) / range) * 100;
                      const uclPosition = ((ucl - minScale) / range) * 100;
                      const currentPosition = ((current - minScale) / range) * 100;

                      return (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              LCL: {lcl.toFixed(4)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              UCL: {ucl.toFixed(4)}
                            </Typography>
                          </Box>
                          {/* Progress bar showing position within scale */}
                          <Box sx={{ position: 'relative', height: 8, bgcolor: 'grey.200', borderRadius: 1 }}>
                            {/* Control limits zone */}
                            <Box
                              sx={{
                                position: 'absolute',
                                left: `${lclPosition}%`,
                                width: `${uclPosition - lclPosition}%`,
                                top: 0,
                                height: '100%',
                                bgcolor: alpha(theme.palette.success.main, 0.2),
                                borderRadius: 0,
                                borderLeft: `2px solid ${theme.palette.error.main}`,
                                borderRight: `2px solid ${theme.palette.error.main}`,
                              }}
                            />
                            {/* Current position indicator */}
                            <Tooltip
                              title={currentProduct.healthReasons ? currentProduct.healthReasons.join(' • ') : `Current: ${current.toFixed(4)}`}
                              placement="top"
                              arrow
                            >
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: -2,
                                  left: `${currentPosition}%`,
                                  width: 12,
                                  height: 12,
                                  bgcolor: getHealthColor(currentProduct.health),
                                  borderRadius: '50%',
                                  transform: 'translateX(-50%)',
                                  border: `2px solid ${theme.palette.background.paper}`,
                                  boxShadow: (current < lcl || current > ucl) ? '0 0 0 2px rgba(255,0,0,0.3)' : 'none',
                                  cursor: 'help',
                                }}
                              />
                            </Tooltip>
                          </Box>
                        </>
                      );
                    })()}
                  </Box>
                </>
              ) : (
                /* Chart View */
                <Box sx={{ my: 0.5, height: 400 }}>
                  <MiniMeterChart
                    meterProductId={currentProduct.meterProductId}
                    meterId={meterId}
                    productType={currentProduct.productType}
                    data={chartData}
                    loading={loadingChart}
                    onClick={() => onViewChart?.(currentProduct.meterProductId, meterId, currentProduct.productType)}
                    statistics={{
                      current_health: shownHealth(currentProduct) === 'unknown' ? 'green' : (shownHealth(currentProduct) as 'green' | 'yellow' | 'red'),
                      trend: currentProduct.statistics.trend === 'unknown' ? 'stable' : currentProduct.statistics.trend,
                      anomaly_count: currentProduct.statistics.anomalyCount,
                      days_since_proving: currentProduct.statistics.daysSinceProving
                    }}
                  />
                </Box>
              )}

              {/* Statistics - only show in numbers view */}
              {viewMode === 'numbers' && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, mt: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Last Proving
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                      {currentProduct.statistics.daysSinceProving} days
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Reports
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                      {currentProduct.statistics.documentCount}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Alerts */}
              {currentProduct.statistics.anomalyCount > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Tooltip
                    title={`Current meter factor is outside control limits. ${currentProduct.statistics.anomalyCount} consecutive reading${currentProduct.statistics.anomalyCount > 1 ? 's' : ''} ${currentProduct.statistics.anomalyCount > 1 ? 'have been' : 'has been'} out of range.`}
                    placement="top"
                  >
                    <Chip
                      label={`${currentProduct.statistics.anomalyCount} consecutive anomal${currentProduct.statistics.anomalyCount === 1 ? 'y' : 'ies'}`}
                      size="small"
                      color="warning"
                      variant="filled"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Tooltip>
                </Box>
              )}

              {currentProduct.statistics.daysSinceProving > 150 && (
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label="Overdue for proving"
                    size="small"
                    color="error"
                    variant="filled"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
              )}
            </motion.div>
          </AnimatePresence>
        </CardContent>
      </Card>

      {/* Maintenance Modal */}
      {selectedProductForMaintenance && (
        <MeterMaintenanceModal
          open={maintenanceModalOpen}
          onClose={handleCloseMaintenance}
          meterProductId={selectedProductForMaintenance.meterProductId}
          meterId={meterId}
          productType={selectedProductForMaintenance.productType}
          currentBaseline={selectedProductForMaintenance.controlLimits.center}
          currentUCL={selectedProductForMaintenance.controlLimits.ucl}
          currentLCL={selectedProductForMaintenance.controlLimits.lcl}
          recentProvings={[
            {
              date: selectedProductForMaintenance.lastProvingDate || new Date().toISOString(),
              meterFactor: selectedProductForMaintenance.currentFactor,
            }
          ]}
          onMaintenanceComplete={handleMaintenanceComplete}
        />
      )}

      {/* Acknowledge warnings on this meter (opened from the ⚠ in the header). */}
      <AcknowledgeWarningPanel
        open={ackPanelOpen}
        meterId={meterId}
        tracks={ackTracks}
        onClose={() => setAckPanelOpen(false)}
        onChanged={() => { onMeterChanged?.(); }}
        onViewHistory={(mpid) => setHistoryTrack(products.find(p => p.meterProductId === mpid) || null)}
      />

      {/* Per-track acknowledgement history + revoke (opened from the Acknowledged chip). */}
      {historyTrack && (
        <AcknowledgementHistory
          open={!!historyTrack}
          meterProductId={historyTrack.meterProductId}
          trackLabel={`${meterId} — ${trackLabel(historyTrack)}`}
          onClose={() => setHistoryTrack(null)}
          onChanged={() => { onMeterChanged?.(); }}
        />
      )}
    </motion.div>
  );
};

export default GroupedMeterTile;